'use client';

import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { HeaderNavbarItem } from './_components/header-navbar-item.component';
import { NavbarProfile } from './_components/header-navbar-profile.component';
import { NavbarSheet } from './_components/header-navbar-sheet.component';
import { navItems } from './_consts/header-nav-items.const';

import logoSvg from '@/assets/medprev/logo.svg?url';
import { Link } from '@/components/ui/link/link.component';
import { useNavigation } from '@/hooks/navigation/use-navigation.hook';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';

const Header = ({
  isActiveStageOne,
}: {
  isActiveStageOne: string | boolean;
}) => {
  const [isHidden, setIsHidden] = useState(false);
  const lastScrollYRef = useRef(0);
  const { getHomeLink } = useNavigation();

  useEffect(() => {
    lastScrollYRef.current = window.scrollY;

    let ticking = false;
    const SCROLL_DELTA = 8;
    const HIDE_OFFSET = 80;

    const handleScroll = () => {
      if (ticking) {
        return;
      }

      ticking = true;
      window.requestAnimationFrame(() => {
        const currentScrollY = window.scrollY;
        const isScrollingDown
          = currentScrollY - lastScrollYRef.current > SCROLL_DELTA;
        const isScrollingUp
          = lastScrollYRef.current - currentScrollY > SCROLL_DELTA;

        if (isScrollingDown && currentScrollY > HIDE_OFFSET) {
          setIsHidden(true);
        } else if (isScrollingUp || currentScrollY <= HIDE_OFFSET) {
          setIsHidden(false);
        }

        lastScrollYRef.current = currentScrollY;
        ticking = false;
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <header
      data-aria-hidden="false"
      aria-hidden="false"
      className={`sticky top-0 z-50 bg-white transition-transform duration-300 ease-in-out ${isHidden ? '-translate-y-full' : 'translate-y-0'
      }`}
    >
      <div className="flex justify-between items-center border-b border-b-gray-200 px-5 md:px-10 h-16 md:h-14">
        <div className="flex items-center gap-4 md:gap-8 h-full">
          <NavbarSheet navItems={navItems} />
          <Link
            prefetch={false}
            href={getHomeLink()}
            data-testid={DataTestIdEnum.HEADER_LINK_LOGO}
            data-analytics-event={AnalyticsEnum.CLICK_HEADER_NAV_ITEM}
            data-analytics-properties={JSON.stringify({ item_header: 'logo medprev' })}
          >
            <Image
              src={logoSvg}
              height={29}
              width={60}
              alt="logo"
              data-testid="logo"
              priority
            />
          </Link>
          <nav className="hidden lg:flex h-full" data-testid="navbar">
            {navItems.map((item) => (
              <HeaderNavbarItem key={item.id} {...item} />
            ))}
          </nav>
        </div>

        <div className="flex items-center gap-4 md:gap-6">
          <NavbarProfile isActiveStageOne={isActiveStageOne} />
        </div>
      </div>
      <div id="header-location-slot" />
    </header>
  );
};

export default Header;
